import { type ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { type PayoutStatus } from '@marketplace/contracts';
import { ContinueWithStripe } from './ContinueWithStripe.js';
import { type OnboardingFrom } from './onboarding.js';

interface PendingProps {
  /** `null` when the status could not be read: nothing is shown, and the API stays the judge. */
  payouts: PayoutStatus | null;
  locale: string;
  from: OnboardingFrom;
}

/**
 * `W5-T01` §4.3 — on `/feed` and `/feed/:id`, while the provider cannot quote or bid yet.
 *
 * The status line says where they are and the button takes them back into Stripe. For `REJECTED`
 * there is no link to offer (`PAYOUTS_ONBOARDING_CLOSED`), so the line is the support sentence alone.
 * `canQuote` is the server's answer, gate included, so a provider it lets through sees nothing.
 */
export function PayoutsPendingBanner({ payouts, locale, from }: PendingProps): ReactElement | null {
  const { t } = useTranslation();
  if (payouts === null || payouts.canQuote) return null;

  return (
    <section
      className="mp-section"
      aria-labelledby="payouts-banner-title"
      data-testid="payouts-banner"
    >
      <h2 id="payouts-banner-title">{t('payouts.banner.title')}</h2>
      <p>{t(`payouts.status.${payouts.status}`)}</p>
      {payouts.status === 'REJECTED' ? null : (
        <ContinueWithStripe locale={locale} from={from} label={t('payouts.action.continue')} />
      )}
    </section>
  );
}

/**
 * `W5-T01` §4.3 — the same message in its short form, under the quote or bid button the provider
 * cannot use yet. The form above stays visible, so they see what they will be able to do.
 */
export function PayoutsCtaLine({ payouts, locale, from }: PendingProps): ReactElement | null {
  const { t } = useTranslation();
  if (payouts === null || payouts.canQuote) return null;

  return (
    <div className="mp-meta" data-testid="payouts-cta">
      {payouts.status === 'REJECTED' ? (
        <p>{t('payouts.status.REJECTED')}</p>
      ) : (
        <>
          <p>{t('payouts.pending.cta')}</p>
          <ContinueWithStripe
            locale={locale}
            from={from}
            label={t('payouts.action.continue')}
            variant="secondary"
          />
        </>
      )}
    </div>
  );
}
